import React from 'react'
import { useSearchParams } from 'react-router'
import { Base, Checkbox } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { useCreateReviews } from '@/hooks'
import Success from '@/assets/images/icons/Success.svg'
import { ProductCard } from '@/ui/components/process-details'
import { NotReturnedProductCard } from '@/ui/components/process-details/not-returned-product-card'
import { RestockItem } from '@/domain/restock'
import { ReturnedItem, ReturnProcess } from '@/domain/returns'
import { useAvailableRestockLocations } from '@/application/queries'
import { NewReview } from '@/domain/review'
import { useAppSelector } from '@/redux/hooks'
import { PbLogisticsSummaryReturnMethodEnum } from '@itsrever/rever-api'
import { SelectStoreDialog } from '../../select-store-dialog/select-store-dialog'

const {
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  Button
} = Base

interface ProductsTabProps {
  processDetails?: ReturnProcess
  restockMode: boolean
  reviewMode: boolean
  setReviewMode: (value: boolean) => void
  returnedItems: ReturnedItem[]
  notReceivedItems: ReturnedItem[]
  reviews: NewReview[]
  setReviews: (reviews: NewReview[]) => void
  restocks: RestockItem[]
  setRestocks: (restocks: RestockItem[]) => void
  showRefundIsland: boolean
  returnMethod?: PbLogisticsSummaryReturnMethodEnum
}

const ProductsTab: React.FC<ProductsTabProps> = ({
  processDetails,
  restockMode,
  reviewMode,
  setReviewMode,
  returnedItems,
  notReceivedItems,
  reviews,
  setReviews,
  restocks,
  setRestocks,
  showRefundIsland,
  returnMethod
}) => {
  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const ecommerceId = useAppSelector((s) => s.generalData.selectedEcommerce)

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const hasApprovedReview = reviews.some((r) => r.status === 'APPROVED')

  const showStoreDialog =
    returnMethod === PbLogisticsSummaryReturnMethodEnum.InStore &&
    !!settings.in_store_settings
      ?.automated_store_to_warehouse_logistics_enabled &&
    hasApprovedReview

  const variantIds = processDetails?.returnedItems.map(
    (item) => item.product.variantId
  )
  const { restockLocations } = useAvailableRestockLocations({
    variantIds,
    orderId: processDetails?.orderId,
    ecommerceId: searchParams.get('ecommerce_id') ?? ecommerceId
  })

  const userSelectedPm =
    processDetails?.refundPaymentMethod ?? 'NO_REFUND_METHOD'

  const {
    mutate: createReview,
    isPending: loadingReview,
    isSuccess: reviewSuccess
  } = useCreateReviews()

  const handleClickCancel = () => {
    setReviewMode(false)
  }
  const handleSubmit = async () => {
    if (reviewMode) {
      createReview({
        processId: processDetails?.processId ?? '',
        reviews,
        refundOverride: {}
      })
      return
    }
  }

  const restockSelectedItems = restocks.filter(
    (r) => r.status === 'SHOULD_RESTOCK'
  ).length

  const handleClickRestockCheckbox = () => {
    if (restockSelectedItems > 0) {
      setRestocks(
        restocks.map((r) => {
          return {
            ...r,
            status: r.status === 'SHOULD_RESTOCK' ? 'NOT_RESTOCKED' : r.status
          }
        })
      )
      return
    }

    setRestocks(
      restocks.map((r) => {
        return {
          ...r,
          status: r.status === 'NOT_RESTOCKED' ? 'SHOULD_RESTOCK' : r.status
        }
      })
    )
  }

  return (
    <div className="h-full">
      {returnedItems && returnedItems.length > 0 && (
        <div className="flex flex-col gap-3">
          {restockMode ? (
            <div className="mb-2">
              <div className="flex w-full items-center gap-2 rounded-lg bg-[#F5F7FAFF] p-1 px-3">
                <Checkbox
                  indeterminate={
                    restockSelectedItems !== returnedItems.length &&
                    restockSelectedItems > 0
                  }
                  checked={restockSelectedItems === returnedItems.length}
                  className="cursor-pointer"
                  onClick={handleClickRestockCheckbox}
                />
                <p className="text-sm text-[#99A0AEFF]">
                  {restockSelectedItems}{' '}
                  {t('products_tab.items_selected_for_restock')}
                </p>
              </div>
            </div>
          ) : null}

          {returnedItems.map((lineItem, i) => {
            return (
              <ProductCard
                key={i}
                index={i}
                lineItem={lineItem}
                userSelectedPm={userSelectedPm}
                reviewMode={reviewMode}
                reviews={reviews}
                setReviews={setReviews}
                restockMode={restockMode}
                newRestocks={restocks}
                setNewRestocks={setRestocks}
                restockLocations={restockLocations}
              />
            )
          })}
        </div>
      )}
      {reviewMode ? (
        <div className="flex w-full justify-center pb-0.5 pt-3">
          {reviewSuccess ? (
            <div className="boder-solid flex items-center rounded-[5rem] border border-[rgb(85_181_118)] text-[0.8rem] text-[rgb(85_181_118)]">
              <img
                className="mr-1 size-6 text-[rgb(85_181_118)]"
                src={Success}
              />
              Success
            </div>
          ) : loadingReview ? (
            <Base.Spinner
              size="lg"
              label={t('products_tab.submitting_review')}
            />
          ) : !showRefundIsland ? (
            <div className="flex w-full items-center justify-center gap-2">
              <Button variant="secondary" onClick={handleClickCancel}>
                {t('products_tab.cancel')}
              </Button>
              {showStoreDialog ? (
                <DialogRoot>
                  <DialogTrigger
                    render={
                      <Button
                        variant="primary"
                        disabled={reviews.some(
                          (r) => !r.status || r.status == 'PENDING'
                        )}
                      />
                    }
                  >
                    <p className="text-sm">{t('products_tab.submit')}</p>
                  </DialogTrigger>
                  <DialogPortal>
                    <DialogBackdrop />
                    <DialogPopup>
                      <SelectStoreDialog
                        onSubmitStore={handleSubmit}
                        isLoading={loadingReview}
                        customerPrintedOrderId={
                          processDetails?.customerPrintedOrderId
                        }
                        ecommerceId={processDetails?.ecommerceId}
                      />
                    </DialogPopup>
                  </DialogPortal>
                </DialogRoot>
              ) : (
                <Button
                  variant="primary"
                  disabled={reviews.some(
                    (r) => !r.status || r.status == 'PENDING'
                  )}
                  onClick={handleSubmit}
                >
                  {t('products_tab.submit')}
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      {notReceivedItems && notReceivedItems.length > 0 && (
        <div className="pt-4">
          <p className="mb-1 text-sm font-semibold">
            {t('products_tab.not_received')}
          </p>
          {notReceivedItems.map((lineItem, i) => {
            return (
              <div className="py-3" key={i}>
                <NotReturnedProductCard
                  lineItem={lineItem}
                  userSelectedPm={userSelectedPm}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ProductsTab
